import { Input } from '@/app/common/components/input/Input'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { ErrorCard } from '@/common/components/ErrorCard'
import LoadingCard from '@/common/components/LoadingCard'
import ApplicationStatusBadge from '@/common/components/StatusBadge'
import { useHasRequiredPermissions } from '@/hook/useHasRequiredRoles'
import ProviderDecisionPopup from '@/pages/providers/decision_popup/View'
import { ProviderQueries } from '@/queries/providers/queries'
import { RootState } from '@/storage/store'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import SchoolInfo from './SchoolInfo'
import EditableField from '@/pages/applicants/applicant_details/components/EditableField.tsx'
import { cn } from '@/lib/utils'
import { z } from 'zod'
import useEditableField from '@/pages/applicants/applicant_details/hooks/useEditableFields.tsx'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateProviderInfo } from '@/pages/providers/components/hooks/useUpdateProviderInfo.ts'
import axios from 'axios'
import AdminAuthGuard from '../admin-auth-guard'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/shad/ui/toast/use-toast'
import { updateProviderStatus } from '@/queries/providers/requests'
import { ProviderQueryKeys } from '@/queries/providers/queries'
import Badge from '@/common/components/Badge'
import { Switch } from '@/common/components/Switch'
import { parseRequestError, getSemanticColor } from '@/app/common/utilities'
import { useState } from 'react'
import { ConfirmDialog } from '@/common/components/confirm-dialog/ConfirmDialog'
import {
  BANK_LINKING_PROVIDER_MANUAL,
  BANK_LINKING_PROVIDER_PLAID,
  resolveBankLinkingProvider,
} from '@/schemas/bank-linking.schema'

const BankAccountSchema = z.object({
  id: z.number(),
  status: z.number(),
  provider_id: z.number(),
  address: z.string(),
  address_2: z.string().nullable(),
  city: z.string(),
  zip_code: z.string(),
  account_number: z.string(),
  routing_number: z.string(),
  bank_name: z.string(),
  account_type_name: z.string(),
  global_state_name: z.string(),
  account_holder_name: z.string(),
  account_holder_dob: z.string().nullable(),
  account_nickname: z.string(),
  verification_status_id: z.number(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
})

const BankAccountResponseSchema = BankAccountSchema.or(z.literal(''))

// Type inference
type BankAccount = z.infer<typeof BankAccountSchema>

export { BankAccountSchema, type BankAccount }

const VendorEditSchema = z.object({
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().email().nullable(),
})

type EditableFields = keyof z.infer<typeof VendorEditSchema> | 'none'

const ProviderInfoTabPage = () => {
  const location = useLocation()
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [thirdPartyDialogOpen, setThirdPartyDialogOpen] = useState(false)
  const [bankLinkingDialogOpen, setBankLinkingDialogOpen] = useState(false)

  const hasRequiredRoles = useHasRequiredPermissions([
    'provider_management_edit',
  ])
  const providerId = location.pathname.split('/')[2]
  const {
    data: providerInfo,
    status,
    error,
  } = useQuery(
    ProviderQueries.getProviderById(selectedProgramYear?.id ?? 0, providerId)
  )

  const { data: bankingInfo, status: bankingInfoStatus } = useQuery({
    queryFn: async () => {
      const response = await axios.get(
        `/admin/providers/${providerId}/banking_information`
      )
      return BankAccountResponseSchema.parse(response.data)
    },
    queryKey: ['banking_information', providerId],
  })

  const { selectedField, startEditing, cancelEditing } =
    useEditableField<EditableFields>({ initialValue: 'none' })

  const { register, getValues } = useForm<z.infer<typeof VendorEditSchema>>({
    resolver: zodResolver(VendorEditSchema),
    defaultValues: {
      address: providerInfo?.address ?? '',
      phone: providerInfo?.phone ?? '',
      email: providerInfo?.email ?? '',
    },
  })

  const updateInlineEdit = useUpdateProviderInfo({
    providerId: providerId ?? '',
    programId: selectedProgramYear?.id ?? 0,
  })

  const updateThirdParty = useUpdateProviderInfo({
    providerId: providerId ?? '',
    programId: selectedProgramYear?.id ?? 0,
    successTitle: 'Third Party Vendor Updated',
    errorTitle: 'Third Party Vendor Update Failed',
    onSuccess: () => setThirdPartyDialogOpen(false),
  })

  const updateBankLinking = useUpdateProviderInfo({
    providerId: providerId ?? '',
    programId: selectedProgramYear?.id ?? 0,
    successTitle: 'Bank Verification Method Updated',
    errorTitle: 'Bank Verification Method Update Failed',
    onSuccess: () => setBankLinkingDialogOpen(false),
  })

  const statusMutation = useMutation({
    mutationFn: (nextStatus: 0 | 1) =>
      updateProviderStatus(providerId, nextStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.details(
          selectedProgramYear?.id ?? 0,
          providerId
        ),
      })
      toast({
        title: 'Provider status updated',
        description: 'Provider status has been updated successfully.',
        variant: 'default',
      })
      setStatusDialogOpen(false)
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : parseRequestError(error).message
      toast({
        title: 'Status update failed',
        description: message,
        variant: 'destructive',
      })
    },
  })

  const getTextValue = (value: string | null | number) => {
    if (typeof value === 'number') {
      return value
    }

    return value && value?.trim().length > 0 ? value : 'Not specified'
  }

  if (status === 'error' || bankingInfoStatus === 'error') {
    return <ErrorCard text={error?.message} />
  }

  if (status === 'pending' || bankingInfoStatus === 'pending') {
    return <LoadingCard title="Loading Provider Details" />
  }

  const statusCode = providerInfo?.status
  const isActive = statusCode === 1
  const nextStatus: 0 | 1 = isActive ? 0 : 1
  const nextLabel = isActive ? 'Disable' : 'Enable'

  const isThirdParty = providerInfo?.is_third_party === true
  const nextThirdParty = !isThirdParty
  const nextThirdPartyLabel = isThirdParty ? 'Disable' : 'Enable'
  const showThirdPartyCard =
    import.meta.env.VITE_ENABLE_THIRD_PARTY_VENDORS !== 'false'

  // Which flow this provider gets when adding a bank account in the provider
  // portal. Plaid is the default; 'manual' sends them through the legacy
  // SoftPoint iframe, which collects the account and routing numbers directly.
  const usesPlaidLinking =
    resolveBankLinkingProvider(providerInfo?.bank_linking_provider) ===
    BANK_LINKING_PROVIDER_PLAID
  const nextBankLinkingProvider = usesPlaidLinking
    ? BANK_LINKING_PROVIDER_MANUAL
    : BANK_LINKING_PROVIDER_PLAID

  return (
    <div>
      <div className="flex justify-end">
        {providerInfo?.status_name === 'Application Submitted' && (
          <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
            <ProviderDecisionPopup />
          </AdminAuthGuard>
        )}
      </div>
      <div>
        <div className="flex flex-col w-full max-w-full gap-4">
          <div
            className={cn('grid gap-4', {
              'sm:grid-cols-2': statusCode === 0 || statusCode === 1,
            })}
          >
            {(statusCode === 0 || statusCode === 1) && (
              <Card className="w-full bg-white shadow-sm">
                <CardHeader>
                  <h3 className="text-lg font-medium">
                    Change Provider Status
                  </h3>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Current Status</p>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge
                          semanticColor={getSemanticColor(
                            isActive ? 'Active' : 'Inactive'
                          )}
                        >
                          {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>

                    <AdminAuthGuard
                      requiredPermissions={['provider_management_edit']}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {isActive ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={isActive}
                          disabled={statusMutation.status === 'pending'}
                          onCheckedChange={() => {
                            setStatusDialogOpen(true)
                          }}
                        />
                      </div>
                    </AdminAuthGuard>
                  </div>

                  <ConfirmDialog
                    open={statusDialogOpen}
                    onOpenChange={setStatusDialogOpen}
                    title={`${nextLabel} Provider`}
                    description={`${providerInfo?.name ?? 'This provider'} will be set to ${
                      nextStatus === 1 ? 'active' : 'inactive'
                    }.`}
                    confirmText={nextLabel}
                    cancelText="Cancel"
                    variant={nextStatus === 1 ? 'default' : 'destructive'}
                    confirmDisabled={statusMutation.status === 'pending'}
                    confirmLoading={statusMutation.status === 'pending'}
                    onConfirm={() => statusMutation.mutate(nextStatus)}
                  />
                </CardContent>
              </Card>
            )}

            {/* Third Party Vendor Card */}
            {showThirdPartyCard && (
              <Card className="w-full bg-white shadow-sm">
                <CardHeader>
                  <h3 className="text-lg font-medium">Third Party Vendor</h3>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500">Current Status</p>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge
                          semanticColor={getSemanticColor(
                            isThirdParty ? 'Active' : 'Inactive'
                          )}
                        >
                          {isThirdParty ? 'Third Party' : 'Not Third Party'}
                        </Badge>
                      </div>
                    </div>

                    <AdminAuthGuard
                      requiredPermissions={['provider_management_edit']}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500">
                          {isThirdParty ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={isThirdParty}
                          disabled={updateThirdParty.status === 'pending'}
                          onCheckedChange={() => setThirdPartyDialogOpen(true)}
                        />
                      </div>
                    </AdminAuthGuard>
                  </div>

                  <ConfirmDialog
                    open={thirdPartyDialogOpen}
                    onOpenChange={setThirdPartyDialogOpen}
                    title={`${nextThirdPartyLabel} Third Party`}
                    description={`${providerInfo?.name ?? 'This provider'} will be ${
                      nextThirdParty
                        ? 'marked as a third party vendor'
                        : 'removed from third party vendors'
                    }.`}
                    confirmText={nextThirdPartyLabel}
                    cancelText="Cancel"
                    variant={nextThirdParty ? 'default' : 'destructive'}
                    confirmDisabled={updateThirdParty.status === 'pending'}
                    confirmLoading={updateThirdParty.status === 'pending'}
                    onConfirm={() => {
                      updateThirdParty.mutate({
                        is_third_party: nextThirdParty,
                      })
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Bank Account Verification Card */}
            <Card className="w-full bg-white shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-medium">
                  Bank Account Verification
                </h3>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Current Method</p>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge
                        semanticColor={getSemanticColor(
                          usesPlaidLinking ? 'Active' : 'Inactive'
                        )}
                      >
                        {usesPlaidLinking ? 'Plaid' : 'SoftPoint Iframe'}
                      </Badge>
                    </div>
                  </div>

                  <AdminAuthGuard
                    requiredPermissions={['provider_management_edit']}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {usesPlaidLinking ? 'Plaid' : 'Legacy'}
                      </span>
                      <Switch
                        checked={usesPlaidLinking}
                        disabled={updateBankLinking.status === 'pending'}
                        onCheckedChange={() => setBankLinkingDialogOpen(true)}
                      />
                    </div>
                  </AdminAuthGuard>
                </div>

                <ConfirmDialog
                  open={bankLinkingDialogOpen}
                  onOpenChange={setBankLinkingDialogOpen}
                  title={
                    usesPlaidLinking
                      ? 'Switch to SoftPoint Iframe'
                      : 'Switch to Plaid'
                  }
                  description={`${providerInfo?.name ?? 'This provider'} will ${
                    usesPlaidLinking
                      ? 'add and re-verify their bank account through the legacy SoftPoint iframe, entering their account and routing numbers directly'
                      : 'add and re-verify their bank account through Plaid'
                  }. Any account they have already linked is unaffected.`}
                  confirmText="Switch"
                  cancelText="Cancel"
                  variant={usesPlaidLinking ? 'destructive' : 'default'}
                  confirmDisabled={updateBankLinking.status === 'pending'}
                  confirmLoading={updateBankLinking.status === 'pending'}
                  onConfirm={() => {
                    updateBankLinking.mutate({
                      bank_linking_provider: nextBankLinkingProvider,
                    })
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Account Owner Information Card */}
          <Card key="account-owner-info" className="w-full bg-white shadow-sm">
            <CardHeader>
              <h3 className="text-lg font-medium">Account Owner</h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 space-y-3">
                  {/* Address */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Address</p>
                    <EditableField<EditableFields>
                      name="address"
                      selectedEditMode={selectedField}
                      canEdit={hasRequiredRoles}
                      onEditClick={() => startEditing('address')}
                      onCancelClick={cancelEditing}
                      onSaveClick={() => {
                        updateInlineEdit.mutate({
                          address: getValues('address'),
                        })
                      }}
                      updateStatus={updateInlineEdit.status}
                      displayElement={
                        <p className="mr-2">
                          {getTextValue(providerInfo?.address)}
                        </p>
                      }
                      editiableElement={
                        <AdminAuthGuard
                          requiredPermissions={['provider_management_edit']}
                        >
                          <Input
                            {...register('address')}
                            placeholder="Address"
                          />
                        </AdminAuthGuard>
                      }
                    />
                  </div>

                  {/* Phone */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <EditableField<EditableFields>
                      name="phone"
                      selectedEditMode={selectedField}
                      canEdit={hasRequiredRoles}
                      onEditClick={() => startEditing('phone')}
                      onCancelClick={cancelEditing}
                      onSaveClick={() => {
                        updateInlineEdit.mutate({
                          phone: getValues('phone'),
                        })
                      }}
                      updateStatus={updateInlineEdit.status}
                      displayElement={
                        <p className="mr-2">
                          {getTextValue(providerInfo?.phone)}
                        </p>
                      }
                      editiableElement={
                        <AdminAuthGuard
                          requiredPermissions={['provider_management_edit']}
                        >
                          <Input
                            {...register('phone')}
                            placeholder="Phone number"
                          />
                        </AdminAuthGuard>
                      }
                    />
                  </div>

                  {/* Email */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">E-mail</p>
                    <EditableField<EditableFields>
                      name="email"
                      selectedEditMode={selectedField}
                      canEdit={hasRequiredRoles}
                      onEditClick={() => startEditing('email')}
                      onCancelClick={cancelEditing}
                      onSaveClick={() => {
                        updateInlineEdit.mutate({
                          email: getValues('email'),
                        })
                      }}
                      updateStatus={updateInlineEdit.status}
                      displayElement={
                        <p className="mr-2">
                          {getTextValue(providerInfo?.email)}
                        </p>
                      }
                      editiableElement={
                        <AdminAuthGuard
                          requiredPermissions={['provider_management_edit']}
                        >
                          <Input
                            {...register('email')}
                            placeholder="Email address"
                          />
                        </AdminAuthGuard>
                      }
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        {providerInfo?.school_application ? (
          <SchoolInfo
            school_info={providerInfo.school_application}
            provider_info={providerInfo}
          />
        ) : (
          <div className="space-y-6 lg:space-y-0 mt-6 lg:grid grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <div className="flex w-full justify-between items-center">
                  <h2 className="text-lg font-bold">Provider Info</h2>
                  <div className="flex items-center">
                    <p className="font-bold mr-1">Status:</p>
                    <ApplicationStatusBadge
                      status={providerInfo.application_status}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p>{providerInfo.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Category</p>
                      <p>{providerInfo.global_business_category_name ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Delivery Method</p>
                      <p>
                        {providerInfo.delivery_methods.length > 0
                          ? providerInfo.delivery_methods.join(', ')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Provider Category</p>
                      <p>
                        {providerInfo.provider_categories.length > 0
                          ? providerInfo.provider_categories.join(', ')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Services Offered</p>
                      <p>
                        {providerInfo.provider_services.length > 0
                          ? providerInfo.provider_services.join(', ')
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Contact Details Section */}
            <Card>
              <CardHeader>
                <h3 className="text-lg font-medium">Contact Details</h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p>
                        {providerInfo.applicant_first_name ?? ''}{' '}
                        {providerInfo.applicant_last_name ?? ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p>{providerInfo.applicant_phone_number ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email Address</p>
                      <p>{providerInfo.email ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Family Point of Contact - Full Name
                      </p>
                      <p>
                        {providerInfo.fpc_first_name ?? ''}{' '}
                        {providerInfo.fpc_last_name ?? ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Family Point of Contact - Email
                      </p>
                      <p>{providerInfo.fpc_email ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Family Point of Contact - Phone Number
                      </p>
                      <p>{providerInfo.fpc_phone_number ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mailing Address</p>
                      <p>{providerInfo.mailing_address ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mailing City</p>
                      <p>{providerInfo.mailing_city ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mailing State</p>
                      <p>{providerInfo.mailing_global_state_name ?? '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mailing Zip</p>
                      <p>{providerInfo.mailing_zip_code ?? '-'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="mt-4">
          <CardHeader>Bank Information </CardHeader>
          {bankingInfo ? (
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-3">
                    Account Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Account Holder</p>
                      <p className="">{bankingInfo.account_holder_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date of Birth</p>
                      <p className="">{bankingInfo.account_holder_dob}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Bank Name</p>
                      <p className="">{bankingInfo.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Account Type</p>
                      <p className="">{bankingInfo.account_type_name}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-3">
                    Banking Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Account Number</p>
                      <p className="">{bankingInfo.account_number}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Routing Number</p>
                      <p className="">{bankingInfo.routing_number}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-3">
                    Address Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Street Address</p>
                      <p className="">{bankingInfo.address}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address Line 2</p>
                      <p className="">{bankingInfo.address_2}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">City</p>
                      <p className="">{bankingInfo.city}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">State & ZIP</p>
                      <p className="">
                        {bankingInfo.global_state_name} • {bankingInfo.zip_code}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          ) : (
            <CardContent>
              <div className="flex justify-center items-center h-ful py-10">
                <p className="text-sm text-gray-500">
                  No bank information found
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  )
}

export default ProviderInfoTabPage
